import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader } from '../../components/common';
import './LandingPageTemplate.css';

function LandingPageTemplate({ data }) {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const handleCta = (href) => {
    if (href.startsWith('mailto:')) {
      window.location.href = href;
    } else {
      navigate(href);
    }
  };

  // Build JSON-LD schemas
  const faqSchema = data.faq?.items?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.faq.items.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "DriftAlarm",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "0",
      "highPrice": "389",
      "priceCurrency": "USD",
      "offerCount": "3"
    },
    "url": "https://www.driftalarm.com"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": data.seo.breadcrumbName || data.seo.title.split('|')[0].trim() }
    ]
  };

  const jsonLd = [softwareSchema, faqSchema, breadcrumbSchema].filter(Boolean);

  const utmSuffix = `?utm_source=landing&utm_medium=organic&utm_campaign=${data.seo.path.replace(/^\//, '')}`;

  return (
    <div className="lp-container">
      <SEOHead
        title={data.seo.title}
        description={data.seo.description}
        path={data.seo.path}
        ogType={data.seo.ogType || 'website'}
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <PublicHeader activeLink="guides" extraLinks={['solutions']} />

      <main className="lp-main">
        {/* Hero */}
        <section className="lp-hero">
          <h1 className="lp-hero-h1">{data.hero.h1}</h1>
          <p className="lp-hero-subtitle">{data.hero.subtitle}</p>
          {data.hero.trustMetric && (
            <div className="lp-trust-strip">{data.hero.trustMetric}</div>
          )}
          <div className="lp-cta-group">
            <button
              className="da-btn da-btn--primary"
              onClick={() => handleCta(data.hero.primaryCta.href + utmSuffix)}
              data-tracking={`lp-cta-${data.seo.path.replace(/^\//, '')}-hero-primary`}
            >
              {data.hero.primaryCta.text}
            </button>
            {data.hero.secondaryCta && (
              <Link
                to={data.hero.secondaryCta.href}
                className="lp-cta-secondary"
                data-tracking={`lp-cta-${data.seo.path.replace(/^\//, '')}-hero-secondary`}
              >
                {data.hero.secondaryCta.text}
              </Link>
            )}
          </div>
        </section>

        {/* Problem Section */}
        {data.problem && (
          <section className="lp-section">
            <h2 className="lp-section-heading">{data.problem.heading}</h2>
            {data.problem.paragraphs.map((p, i) => (
              <p key={i} className="lp-paragraph">{p}</p>
            ))}
            {data.problem.stats && (
              <div className="lp-problem-stats">
                {data.problem.stats.map((stat, i) => (
                  <div key={i} className="lp-stat-card">
                    <div className="lp-stat-value">{stat.value}</div>
                    <div className="lp-stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* How It Works */}
        {data.howItWorks && (
          <section className="lp-section">
            <h2 className="lp-section-heading">{data.howItWorks.heading}</h2>
            <div className="lp-steps">
              {data.howItWorks.steps.map((step, i) => (
                <div key={i} className="lp-step">
                  <div className="lp-step-number">{i + 1}</div>
                  <div className="lp-step-content">
                    <div className="lp-step-title">{step.title}</div>
                    <p className="lp-step-description">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Capabilities */}
        {data.capabilities && (
          <section className="lp-section">
            <h2 className="lp-section-heading">{data.capabilities.heading}</h2>
            <div className="lp-capabilities">
              {data.capabilities.items.map((item, i) => (
                <div key={i} className="lp-capability-card">
                  <h3 className="lp-capability-title">{item.title}</h3>
                  <p className="lp-capability-description">{item.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Mid-Page CTA */}
        <section className="lp-mid-cta">
          <div className="lp-mid-cta-content">
            <h2 className="lp-mid-cta-heading">See Your Attack Surface — Start Free Trial</h2>
            <button
              className="da-btn da-btn--primary"
              onClick={() => handleCta(`/upgrade${utmSuffix}`)}
              data-tracking={`lp-cta-${data.seo.path.replace(/^\//, '')}-mid`}
            >
              Start Free 30-Day Trial
            </button>
          </div>
        </section>

        {/* Comparison Table */}
        {data.comparison && (
          <section className="lp-section">
            <h2 className="lp-section-heading">{data.comparison.heading}</h2>
            <div className="guide-table-wrapper">
              <table className="guide-table">
                <thead>
                  <tr>
                    {data.comparison.columns.map((col, i) => (
                      <th key={i}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.comparison.rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Use Cases */}
        {data.useCases && (
          <section className="lp-section">
            <h2 className="lp-section-heading">{data.useCases.heading}</h2>
            <div className="lp-use-cases">
              {data.useCases.cases.map((useCase, i) => (
                <div key={i} className="lp-use-case-card">
                  <h3 className="lp-use-case-persona">{useCase.persona}</h3>
                  <p className="lp-use-case-description">{useCase.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FAQ */}
        {data.faq && (
          <section className="lp-section lp-faq">
            <h2 className="lp-section-heading">{data.faq.heading}</h2>
            <div className="lp-faq-list">
              {data.faq.items.map((item, i) => (
                <div
                  key={i}
                  className={`lp-faq-item ${openFaq === i ? 'lp-faq-item-open' : ''}`}
                >
                  <button
                    className="lp-faq-question"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span>{item.question}</span>
                    <span className="lp-faq-toggle">{openFaq === i ? '−' : '+'}</span>
                  </button>
                  {openFaq === i && (
                    <div className="lp-faq-answer">{item.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Related Resources */}
        {data.relatedPages && (
          <section className="lp-section lp-related">
            <h2 className="lp-section-heading">Related Resources</h2>
            <div className="lp-related-grid">
              {data.relatedPages.map((page, i) => (
                <Link key={i} to={page.href} className="lp-related-link">
                  <span className="lp-related-title">{page.title}</span>
                  <span className="lp-related-arrow">&rarr;</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        {data.bottomCta && (
          <section className="lp-bottom-cta">
            <h2 className="lp-bottom-cta-heading">{data.bottomCta.heading}</h2>
            <p className="lp-bottom-cta-subtitle">{data.bottomCta.subtitle}</p>
            <div className="lp-cta-group">
              <button
                className="da-btn da-btn--primary"
                onClick={() => handleCta(data.bottomCta.primaryCta.href + utmSuffix)}
                data-tracking={`lp-cta-${data.seo.path.replace(/^\//, '')}-bottom-primary`}
              >
                {data.bottomCta.primaryCta.text}
              </button>
              {data.bottomCta.secondaryCta && (
                <a
                  href={data.bottomCta.secondaryCta.href}
                  className="lp-cta-secondary"
                  data-tracking={`lp-cta-${data.seo.path.replace(/^\//, '')}-bottom-secondary`}
                >
                  {data.bottomCta.secondaryCta.text}
                </a>
              )}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-footer-content">
          <div className="lp-footer-brand">DriftAlarm</div>
          <div className="lp-footer-columns">
            <div className="lp-footer-column">
              <div className="lp-footer-column-title">Solutions</div>
              <Link to="/attack-surface-monitoring" className="lp-footer-link">Attack Surface Monitoring</Link>
              <Link to="/external-attack-surface-management" className="lp-footer-link">EASM Platform</Link>
              <Link to="/domain-monitoring" className="lp-footer-link">Domain Monitoring</Link>
              <Link to="/external-vulnerability-scanning" className="lp-footer-link">Vulnerability Scanning</Link>
            </div>
            <div className="lp-footer-column">
              <div className="lp-footer-column-title">Guides</div>
              <Link to="/guides" className="lp-footer-link">All Security Guides</Link>
              <Link to="/guides/attack-surface-monitoring" className="lp-footer-link">Attack Surface Monitoring</Link>
              <Link to="/guides/configuration-drift" className="lp-footer-link">Configuration Drift</Link>
              <Link to="/guides/exposed-rdp" className="lp-footer-link">Exposed RDP Detection</Link>
            </div>
            <div className="lp-footer-column">
              <div className="lp-footer-column-title">Company</div>
              <Link to="/how-it-works" className="lp-footer-link">How It Works</Link>
              <Link to="/upgrade" className="lp-footer-link">Pricing</Link>
              <a href="mailto:support@driftalarm.com" className="lp-footer-link">Contact</a>
              <Link to="/privacy" className="lp-footer-link">Privacy Policy</Link>
              <Link to="/terms" className="lp-footer-link">Terms of Service</Link>
              <Link to="/acceptable-use" className="lp-footer-link">Acceptable Use</Link>
              <Link to="/dpa" className="lp-footer-link">DPA</Link>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span className="lp-footer-copyright">&copy; {new Date().getFullYear()} DriftAlarm</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPageTemplate;
